// F7: offline-first sync. Edits are applied to IndexedDB immediately and queued in
// an outbox; sync() sends the outbox in order, then pulls server changes.
//
// Conflict rule: the server wins. A local edit made against an old version is
// dropped, the server copy replaces it, and the user is told what happened.
import { applyOutcome, nextNoticeDeadline, type Contract, type ContractFields, type Outcome } from "../../shared/contract";
import { todayIn } from "../../shared/dates";
import { ApiError, api } from "./api";
import { getMeta, setMeta, type ClauseKeepDB, type LocalContract, type OutboxEntry, type OutboxOp } from "./db";
import { reportError } from "./report";

export type SyncState = "idle" | "syncing" | "offline" | "error" | "signed_out";

export interface SyncStatus {
  state: SyncState;
  pending: number;
  lastSyncedAt: string | null;
  error: string | null;
}

type Request = typeof api;

export class SyncEngine {
  private running: Promise<void> | null = null;
  /** Outbox id of the request currently on the wire; it must not be edited or dropped. */
  private inflight: number | null = null;
  private again = false;
  private listeners = new Set<(s: SyncStatus) => void>();
  status: SyncStatus = { state: "idle", pending: 0, lastSyncedAt: null, error: null };

  constructor(
    readonly db: ClauseKeepDB,
    private readonly request: Request = api,
    private readonly timezone: () => string = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  ) {}

  subscribe(fn: (s: SyncStatus) => void): () => void {
    this.listeners.add(fn);
    fn(this.status);
    return () => this.listeners.delete(fn);
  }

  private async setStatus(patch: Partial<SyncStatus>) {
    const pending = await this.db.outbox.count();
    this.status = { ...this.status, ...patch, pending };
    for (const fn of this.listeners) fn(this.status);
  }

  private today() {
    return todayIn(this.timezone());
  }

  // --- local mutations ---------------------------------------------------------

  async createContract(id: string, fields: ContractFields, userId: string | null): Promise<LocalContract> {
    const now = new Date().toISOString();
    const local: LocalContract = {
      ...fields,
      id,
      nextNoticeDeadline: nextNoticeDeadline(fields, this.today()),
      version: 0,
      seq: 0,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      documents: [],
      pending: true,
    };
    await this.db.transaction("rw", this.db.contracts, this.db.outbox, async () => {
      await this.db.contracts.put(local);
      await this.enqueue({ kind: "create", contractId: id, fields });
    });
    void this.sync();
    return local;
  }

  async updateContract(id: string, patch: Partial<ContractFields>): Promise<void> {
    await this.db.transaction("rw", this.db.contracts, this.db.outbox, async () => {
      const current = await this.db.contracts.get(id);
      if (!current || current.deletedAt) throw new Error("That contract no longer exists.");
      const merged = { ...current, ...patch };
      await this.db.contracts.put({ ...merged, nextNoticeDeadline: nextNoticeDeadline(merged, this.today()), pending: true, updatedAt: new Date().toISOString() });

      // Coalesce: fold into a queued create or the latest queued update for this contract.
      const ops = await this.db.outbox.where("contractId").equals(id).sortBy("id");
      const last = ops.at(-1);
      if ((last?.kind === "create" || last?.kind === "update") && last.id !== this.inflight) {
        const merged: OutboxEntry = last.kind === "create" ? { ...last, fields: { ...last.fields, ...patch } } : { ...last, fields: { ...last.fields, ...patch } };
        await this.db.outbox.put(merged);
      } else await this.enqueue({ kind: "update", contractId: id, baseVersion: current.version, fields: patch });
    });
    void this.sync();
  }

  async deleteContract(id: string): Promise<void> {
    await this.db.transaction("rw", this.db.contracts, this.db.outbox, async () => {
      const ops = await this.db.outbox.where("contractId").equals(id).toArray();
      const create = ops.find((o) => o.kind === "create");
      if (create && create.id !== this.inflight) {
        // Never reached the server: forget it entirely.
        await this.db.outbox.bulkDelete(ops.map((o) => o.id!));
        await this.db.contracts.delete(id);
        return;
      }
      // Anything already on the wire still lands, so follow it with a delete.
      await this.db.outbox.bulkDelete(ops.filter((o) => o.id !== this.inflight).map((o) => o.id!));
      await this.db.contracts.update(id, { deletedAt: new Date().toISOString(), pending: true });
      await this.enqueue({ kind: "delete", contractId: id });
    });
    void this.sync();
  }

  async recordOutcome(id: string, outcome: Outcome, newEndDate: string | null): Promise<void> {
    await this.db.transaction("rw", this.db.contracts, this.db.outbox, async () => {
      const current = await this.db.contracts.get(id);
      if (!current || current.deletedAt) throw new Error("That contract no longer exists.");
      const result = applyOutcome(current, outcome, this.today(), newEndDate);
      if (!result.ok) throw new Error(result.error);
      const merged = { ...current, ...result.fields };
      await this.db.contracts.put({ ...merged, nextNoticeDeadline: nextNoticeDeadline(merged, this.today()), pending: true });
      await this.enqueue({ kind: "outcome", contractId: id, baseVersion: current.version, outcome, newEndDate });
    });
    void this.sync();
  }

  private async enqueue(op: OutboxOp) {
    await this.db.outbox.add({ ...op, createdAt: new Date().toISOString(), attempts: 0 } as OutboxEntry);
  }

  // --- sync ----------------------------------------------------------------------

  /** Runs one sync; concurrent calls share it, and a call during a run schedules one more. */
  sync(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      do {
        this.again = false;
        await this.runOnce();
      } while (this.again);
    })().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async runOnce() {
    await this.setStatus({ state: "syncing" });
    try {
      const pushed = await this.push();
      if (!pushed) return;
      await this.pull();
      await this.setStatus({ state: "idle", lastSyncedAt: new Date().toISOString(), error: null });
    } catch (err) {
      await this.handleFailure(err);
    }
  }

  private async handleFailure(err: unknown) {
    // Anything that isn't an API answer is a bug or a storage problem (IndexedDB full, private mode…).
    if (!(err instanceof ApiError)) reportError("sync", err);
    if (err instanceof ApiError && err.status === 401) await this.setStatus({ state: "signed_out", error: err.message });
    else if (err instanceof ApiError && (err.offline || err.status >= 500 || err.status === 429))
      await this.setStatus({ state: "offline", error: err.message });
    else await this.setStatus({ state: "error", error: err instanceof Error ? err.message : String(err) });
  }

  private async note(contractId: string, title: string, message: string) {
    await this.db.conflicts.add({ contractId, title, message, createdAt: new Date().toISOString() });
  }

  private async dropOpsFor(contractId: string) {
    const ids = (await this.db.outbox.where("contractId").equals(contractId).primaryKeys()) as number[];
    await this.db.outbox.bulkDelete(ids);
  }

  private async storeServer(contract: Contract) {
    const stillPending = (await this.db.outbox.where("contractId").equals(contract.id).count()) > 0;
    const local = await this.db.contracts.get(contract.id);
    if (stillPending && local?.deletedAt) {
      // A delete is queued: keep it hidden, but learn the server version for the delete.
      await this.db.contracts.put({ ...local, version: contract.version });
      return;
    }
    if (stillPending && local) {
      // Newer local edits are queued: keep them on screen, but learn the server version.
      await this.db.contracts.put({ ...local, version: contract.version, seq: contract.seq, documents: contract.documents });
      return;
    }
    if (contract.deletedAt) await this.db.contracts.delete(contract.id);
    else await this.db.contracts.put({ ...contract, pending: stillPending });
  }

  /** Sends queued edits in order. Returns false when it had to stop (offline, signed out). */
  async push(): Promise<boolean> {
    for (;;) {
      const op = await this.db.outbox.orderBy("id").first();
      if (!op) return true;
      const local = await this.db.contracts.get(op.contractId);
      const title = local?.title ?? "A contract";
      this.inflight = op.id!;
      try {
        const contract = await this.send(op, local);
        await this.db.outbox.delete(op.id!);
        this.inflight = null;
        if (contract) await this.storeServer(contract);
        else await this.db.contracts.delete(op.contractId);
      } catch (err) {
        this.inflight = null;
        if (!(err instanceof ApiError)) throw err;
        if (err.offline || err.status >= 500 || err.status === 429 || err.status === 401) {
          await this.db.outbox.update(op.id!, { attempts: op.attempts + 1, lastError: err.message });
          throw err;
        }
        await this.resolveRejection(op, err, title);
      }
      await this.setStatus({});
    }
  }

  private async send(op: OutboxEntry, local: LocalContract | undefined): Promise<Contract | null> {
    const id = op.contractId;
    switch (op.kind) {
      case "create":
        return (await this.request<{ contract: Contract }>("/api/contracts", { method: "POST", json: { id, ...op.fields } })).contract;
      case "update":
        return (
          await this.request<{ contract: Contract }>(`/api/contracts/${id}`, {
            method: "PATCH",
            json: { ...op.fields, version: local?.version ?? op.baseVersion },
          })
        ).contract;
      case "outcome":
        return (
          await this.request<{ contract: Contract }>(`/api/contracts/${id}/outcome`, {
            method: "POST",
            json: { outcome: op.outcome, newEndDate: op.newEndDate, version: local?.version ?? op.baseVersion },
          })
        ).contract;
      case "delete":
        try {
          await this.request(`/api/contracts/${id}`, { method: "DELETE" });
        } catch (err) {
          if (!(err instanceof ApiError && err.status === 404)) throw err;
        }
        return null;
    }
  }

  /** The server refused an edit for good: undo it locally and tell the user why. */
  private async resolveRejection(op: OutboxEntry, err: ApiError, title: string) {
    const id = op.contractId;
    if (err.status === 409 && err.code === "version_conflict" && err.body.contract) {
      await this.dropOpsFor(id);
      await this.storeServer(err.body.contract as Contract);
      await this.note(id, title, `“${title}” was changed on another device, so your offline changes weren't saved. Check it and edit again if needed.`);
      return;
    }
    if (err.status === 404 || (err.status === 409 && err.code === "deleted")) {
      await this.dropOpsFor(id);
      await this.db.contracts.delete(id);
      await this.note(id, title, `“${title}” was deleted on another device, so your changes to it were discarded.`);
      return;
    }
    // Validation or permission problems: drop this edit and restore the server copy.
    await this.db.outbox.delete(op.id!);
    if (op.kind === "create") {
      await this.dropOpsFor(id);
      await this.db.contracts.delete(id);
    } else {
      try {
        const { contract } = await this.request<{ contract: Contract }>(`/api/contracts/${id}`);
        await this.storeServer(contract);
      } catch {
        // Leave the local copy; the next pull will fix it.
      }
    }
    await this.note(id, title, `Couldn't save “${title}”: ${err.message}`);
  }

  async pull(): Promise<void> {
    let since = await getMeta<number>(this.db, "cursor", 0);
    for (let page = 0; page < 1000; page++) {
      const res = await this.request<{ contracts: Contract[]; cursor: number; more: boolean }>(`/api/sync?since=${since}`);
      const pendingIds = new Set((await this.db.outbox.toArray()).map((o) => o.contractId));
      await this.db.transaction("rw", this.db.contracts, this.db.meta, async () => {
        for (const c of res.contracts) {
          if (pendingIds.has(c.id)) continue; // local edits win until they're pushed
          if (c.deletedAt) await this.db.contracts.delete(c.id);
          else await this.db.contracts.put({ ...c, pending: false });
        }
        await setMeta(this.db, "cursor", res.cursor);
      });
      since = res.cursor;
      if (!res.more) break;
    }
  }

  /** Re-downloads everything (Settings → "Refresh from server"). */
  async resetAndPull(): Promise<void> {
    await setMeta(this.db, "cursor", 0);
    await this.sync();
  }
}
